<template>
  <section>
    <div class="logo-wrapper">
      <img alt="Harrity logo" src="./../assets/logo.svg" />
    </div>
    <header>
      <h1>Hey! Do we know you?</h1>
    </header>

    <form v-if="showLocalProvider" class="provider" @submit.prevent="submit">
      <div>
        <p-text-field v-model="email" :label="'Email'" :test-id="'email'" />
      </div>
      <div>
        <p-text-field v-model="password" :label="'Password'" type="password" :test-id="'password'" />
      </div>
      <div v-if="isRequestFailed" class="error">Authentication failed. Check your email/password and try again.</div>
      <div class="form-row submit">
        <p-button class="primary" :disabled="!isValid || isRequestPending" :test-id="'login'">Let me in!</p-button>
      </div>
    </form>
    <div v-if="hasProvider('microsoft')" class="provider external">
      <p-button class="default microsoft" :disabled="isRequestPending" @click.stop.prevent="microsoft">Sign in with Microsoft</p-button>
    </div>
    <div v-for="provider in externalProviders" :key="provider.name" class="provider external">
      <p-button class="external-provider" :disabled="isRequestPending" @click.stop.prevent="loginWithExternal(provider.type, provider.name)">
        Sign in with {{ provider.name }}
      </p-button>
    </div>
    <div v-if="showOtherLoginOptionsButton" class="provider external other-login-options">
      <button type="button" :disabled="isRequestPending" @click.stop.prevent="showOtherLoginOptions = true">other login options</button>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import Button from './common/Button';
import TextField from './common/TextField';
export default {
  components: {
    'p-button': Button,
    'p-text-field': TextField
  },
  data() {
    return {
      email: '',
      password: '',
      showOtherLoginOptions: false
    };
  },
  computed: {
    ...mapState({
      isRequestPending: s => s.identity.isRequestPending,
      isRequestFailed: s => s.identity.isRequestFailed,
      providers: s => s.authentication.providers
    }),
    isValid() {
      return this.email.length && this.password.length;
    },
    localProvider() {
      return this.providers.find((p) => p.type === 'local');
    },
    showLocalProvider() {
      if (!this.localProvider) {
        return false;
      }

      const isShownByDefault = this.localProvider.showByDefault !== false;
      return isShownByDefault || this.showOtherLoginOptions;
    },
    showOtherLoginOptionsButton() {
      if (!this.localProvider) {
        return false;
      }

      return this.localProvider.showByDefault === false && !this.showOtherLoginOptions;
    },
    externalProviders() {
      if (!this.providers || !Array.isArray(this.providers)) {
        return [];
      }
      return this.providers.filter(p => p.type !== 'local' && p.type !== 'microsoft');
    }
  },
  async created() {
    await this.$store.dispatch('authentication/initialize');
  },
  methods: {
    hasProvider(name) {
      if (!this.providers || !Array.isArray(this.providers)) {
        return false;
      }
      return this.providers.some(p => p.type === name);
    },
    async submit() {
      if (!this.isValid) {
        return;
      }

      await this.$store.dispatch('identity/login', { email: this.email, password: this.password });

      this.$identify(this.email);

      await this.$store.dispatch('directoryIndex/initialize');
      this.$trackEvent('Login with email/password');
    },
    async microsoft() {
      const response = await fetch(`/api/auth/microsoft?redirect_uri=${document.location.origin}/microsoft.html`);
      if (response.status < 200 || response.status > 300) {
        this.$toast.error({
          title: 'Failed to Sign in with Microsoft',
          message: `Please, try again later or contact our development team.`
        });
        return;
      }

      this.$trackEvent('Login with Microsoft');

      const url = await response.text();
      window.location.href = url;

      await this.$store.dispatch('directoryIndex/initialize');
    },
    async loginWithExternal(providerType) {
      if (providerType === 'saml') {
        const redirectUri = encodeURIComponent(`${document.location.origin}/saml-callback.html`);
        window.location.href = `/api/auth/saml/login?redirect_uri=${redirectUri}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
section {
  min-height: 100%;
  background-color: var(--theme-surface);

  header {
    width: 100%;
    padding: 1rem 0;
  }
  h1 {
    color: var(--theme-on-surface);
    text-align: center;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  .logo-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
  img {
    max-width: 200px;
    max-height: 200px;
  }

  form {
    max-width: 480px;
    margin: auto;
    padding: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid var(--theme-highlight);

    > * {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
      &.submit {
        display: flex;
        justify-content: center;
      }
    }

    .error {
      font-size: 0.8rem;
      color: var(--theme-error);
      text-align: center;
      display: block;
    }
  }

  .provider {
    &:not(:last-child) {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
    }
    &:last-child {
      padding-bottom: 2rem;
      border-bottom: 2px solid var(--theme-highlight);
    }
  }
    .external {
      display: flex;
      justify-content: center;
    .microsoft {
      &:before {
        content: '';
        background-image: url('~./../assets/microsoft.svg');
        width: 21px;
        height: 21px;
        margin-right: 12px;
      }
      background: #2f2f2f;
      color: #fff;
      font-weight: 600;
      font-size: 15px;
      line-height: 1;
      text-transform: inherit;
      &:hover {
        background: #2f2f2f !important;
      }
    }
    .external-provider {
      background: #2f2f2f;
      color: #fff;
      font-weight: 600;
      font-size: 15px;
      line-height: 1;
      text-transform: inherit;
      &:hover {
        background: #2f2f2f !important;
      }
    }

    &.other-login-options {
      button {
        border: 0;
        background: transparent;
        color: #00a3e0;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
}
</style>
